/** @prettier */
/**
 * 室内機のデマンド制御ダイアログ
 */

import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Box,
  Switch,
  Grid,
  ToggleButton,
  ToggleButtonGroup,
  Stack,
  List,
  ListItem,
} from '@mui/material';
import {useEffect, useState} from 'react';

//MTH component import
import TextFieldSelectBox from '../../component/SelectBox';
import APIErrorMessage from '../../component/APIErrorMessage';
import {PrimaryButton, SecondaryButton} from '../../component/BasicButton';
//API import
import {getDemandSetting, patchDemandSetting} from '../../API/API_Collection';
//interface import
import * as apiIF from '../../Type/api';
//Style import
import '../../css/common/common.css';
import * as React from 'react';
import {pullDownMenu} from '../../define';
import {useTranslation} from 'react-i18next';

/**
 * デマンドデータをフロアでソート
 * @param groupData 室内機のグループ情報
 * @returns ソートされたグループ情報
 */
const sortUnitList = (
  groupData: apiIF.res.getDemandSetting['group-informations']
): apiIF.res.getDemandSetting['group-informations'] => {
  /** ユニット受信データ */
  //ソートで配列が破壊されるので配列をコピー
  let localGroupData = groupData.concat();

  //フロア階層とグループ名でソート
  localGroupData.sort((a, b) => {
    //先にフロア階数で降順ソート
    if (a.floor !== b.floor) {
      return Number(b.floor) - Number(a.floor);
    }
    //フロア階層ソート後にグループ名で昇順ソート
    if (a['group-name'] !== b['group-name']) {
      return a['group-name'] < b['group-name'] ? -1 : 1;
    }
    return 0;
  });
  return localGroupData;
};

//ユニット名・デマンドレベルの幅設定
const gridXsUnitName = 4.5;
const gridXsDemandControlLevel = 7.5;

/**
 * 室内機のデマンド設定ダイアログ
 * @param props
 * @returns ダイアログの表示画面
 */
const IndoorUnitDemandControlDialog = (props: {
  allDemandInfo: apiIF.res.getDemandSetting; //デマンド情報
  setAllDemandInfo: React.Dispatch<
    React.SetStateAction<apiIF.res.getDemandSetting | undefined> //デマンド情報の設定関数
  >;
  isOpenIndoorDialog: boolean; //ダイアログが開いているかどうかの判定
  setIsOpenIndoorDialog: (arg0: boolean) => void; //ダイアログが開いているかどうかの判定の設定関数
  setUnitCount: React.Dispatch<
    React.SetStateAction<{
      ou: {
        lv0: number;
        lv1: number;
        lv2: number;
        lv3: number;
        all: number;
      };
      iu: {
        lv0: number;
        lv1: number;
        lv2: number;
        lv3: number;
        all: number;
      };
    }>
  >; //ユニット数の設定関数
  demandImplementation: true | false; //デマンド実施状況
}) => {
  const trans = useTranslation().t; //翻訳関数

  //フロア名変換テーブル
  const buildingBasementName: {[key: string]: string} = {
    '14': trans('IndoorUnitDemandControlDialog.unitSetting.table.data.floor14'),
    '13': trans('IndoorUnitDemandControlDialog.unitSetting.table.data.floor13'),
    '12': trans('IndoorUnitDemandControlDialog.unitSetting.table.data.floor12'),
    '11': trans('IndoorUnitDemandControlDialog.unitSetting.table.data.floor11'),
    '10': trans('IndoorUnitDemandControlDialog.unitSetting.table.data.floor10'),
    '9': trans('IndoorUnitDemandControlDialog.unitSetting.table.data.floor9'),
    '8': trans('IndoorUnitDemandControlDialog.unitSetting.table.data.floor8'),
    '7': trans('IndoorUnitDemandControlDialog.unitSetting.table.data.floor7'),
    '6': trans('IndoorUnitDemandControlDialog.unitSetting.table.data.floor6'),
    '5': trans('IndoorUnitDemandControlDialog.unitSetting.table.data.floor5'),
    '4': trans('IndoorUnitDemandControlDialog.unitSetting.table.data.floor4'),
    '3': trans('IndoorUnitDemandControlDialog.unitSetting.table.data.floor3'),
    '2': trans('IndoorUnitDemandControlDialog.unitSetting.table.data.floor2'),
    '1': trans('IndoorUnitDemandControlDialog.unitSetting.table.data.floor1'),
    '0': trans('IndoorUnitDemandControlDialog.unitSetting.table.data.floor0'),
    '-1': trans(
      'IndoorUnitDemandControlDialog.unitSetting.table.data.basement1'
    ),
    '-2': trans(
      'IndoorUnitDemandControlDialog.unitSetting.table.data.basement2'
    ),
    '-3': trans(
      'IndoorUnitDemandControlDialog.unitSetting.table.data.basement3'
    ),
    '-4': trans(
      'IndoorUnitDemandControlDialog.unitSetting.table.data.basement4'
    ),
  };

  /**各レベルコンポーネントに使用する配列 */
  const levelAll: ('level1' | 'level2' | 'level3' | 'level0')[] = [
    'level1',
    'level2',
    'level3',
    'level0',
  ];

  /**各レベルに対応する表示 */
  const levelDisp = {
    level1: trans(
      'IndoorUnitDemandControlDialog.unitSetting.table.data.level1'
    ),
    level2: trans(
      'IndoorUnitDemandControlDialog.unitSetting.table.data.level2'
    ),
    level3: trans(
      'IndoorUnitDemandControlDialog.unitSetting.table.data.level3'
    ),
    level0: trans(
      'IndoorUnitDemandControlDialog.unitSetting.table.data.level0'
    ),
  };
  /**有効/無効に対応する表示 */
  const activeDisp = {
    active: trans('IndoorUnitDemandControlDialog.modeSetting.dispActive'),
    inactive: trans('IndoorUnitDemandControlDialog.modeSetting.dispInactive'),
  };
  //active / inactiveの設定
  const [controlIndoorSwitching, setControlIndoorSwitching] = useState(
    props.allDemandInfo['demand']['indoor-unit']
  );

  // 温度シフトの設定
  const [shiftTemp, setShiftTemp] = useState(
    props.allDemandInfo['demand']['shift-temperature']
  );

  // 室内機グループの情報
  const [demandGroupInfo, setDemandGroupInfo] = useState<
    apiIF.res.getDemandSetting['group-informations']
  >(sortUnitList(props.allDemandInfo['group-informations']));

  // 全ユニット一括のデマンドレベル設定用
  const [stateButtonAllDemandLevel, setStateButtonAllDemandLevel] = useState<
    'level0' | 'level1' | 'level2' | 'level3' | null
  >(null);

  const [saveBtnDisabled, setSaveBtnDisabled] = useState(false); //SAVEボタン非表示フラグ
  const [isOpenErrorDialog, setIsOpenErrorDialog] = useState(false); //APIエラー時のエラーダイアログ表示フラグ
  const [apiWait, setAPIWait] = useState(false); //API応答待ちフラグ

  //室内機のユニット数の計算
  useEffect(() => {
    props.setUnitCount(prevCount => ({
      ...prevCount,
      iu: countIndoorUnit(props.allDemandInfo['group-informations']),
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /**
   * モードのスイッチを切り替える関数
   * @param event スイッチの入力のイベント
   */
  const handleChangeAutoSwitching = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    event.target.checked
      ? setControlIndoorSwitching('active')
      : setControlIndoorSwitching('inactive');
  };

  /**
   * 全ユニットのデマンドレベルのボタン押下時処理
   * @param event ボタン押下時のイベント
   * @param demandLevel デマンドレベル
   */
  const onClickAllDemand = (
    event: React.MouseEvent<HTMLElement>,
    demandLevel: 'level0' | 'level1' | 'level2' | 'level3' | null
  ) => {
    /**
     * 全ユニットのデマンドレベルが連続して押された場合はnullが入力されるので
     * その場合は以前選択したデマンドレベルに再度設定するようにする。
     * demandLevelとstateButtonAllDemandLevelが共にnullとなる場合は初期値が
     * 設定されるが、そのような場合はあり得ない。
     */
    let targetDemandLevel: 'level0' | 'level1' | 'level2' | 'level3' = 'level0';
    if (demandLevel === null && stateButtonAllDemandLevel !== null) {
      targetDemandLevel = stateButtonAllDemandLevel;
    } else if (demandLevel !== null) {
      targetDemandLevel = demandLevel;
    }
    setStateButtonAllDemandLevel(targetDemandLevel);
    setDemandGroupInfo(
      demandGroupInfo.map(groupInfo => ({
        ...groupInfo,
        'sl-indoor-informations': groupInfo['sl-indoor-informations'].map(
          slInfo => {
            return {...slInfo, 'demand-level': targetDemandLevel};
          }
        ),
        'slif-indoor-informations': groupInfo['slif-indoor-informations'].map(
          slifInfo => {
            if (slifInfo['interface-type'] === 'SC-ADNA') {
              //SC-ADNAの場合
              return {...slifInfo, 'demand-level': targetDemandLevel};
            } else if (slifInfo['interface-type'] === 'other') {
              //otherの場合
              if (
                targetDemandLevel === 'level3' ||
                targetDemandLevel === 'level0'
              ) {
                return {...slifInfo, 'demand-level': targetDemandLevel};
              }
            }
            return slifInfo;
          }
        ),
      }))
    );
  };

  /**
   * CANCELボタン押下時処理
   */
  const handleCancel = () => {
    props.setIsOpenIndoorDialog(false);
    /** ポップアップを閉じると、適応していないデータは破棄
     * allDemandInfoはソートしていないので再度group-informationsをソート
     */
    setDemandGroupInfo(sortUnitList(props.allDemandInfo['group-informations']));
    setControlIndoorSwitching(props.allDemandInfo['demand']['indoor-unit']);
    setShiftTemp(props.allDemandInfo['demand']['shift-temperature']);
    setStateButtonAllDemandLevel(null);
  };

  /**
   * SAVEボタン押下時処理
   */
  const onClickSave = () => {
    setSaveBtnDisabled(true);
    setAPIWait(true);

    const editData: apiIF.reqParameters.patchDemandSetting = {
      'sl-indoor-informations': [],
      'slif-indoor-informations': [],
    };

    // デマンド情報更新
    demandGroupInfo.forEach(groupInfo => {
      groupInfo['sl-indoor-informations'].forEach(slInfo => {
        const tmp = {
          'demand-level': slInfo['demand-level'],
          'unit-id': slInfo['unit-id'],
        };
        if (editData['sl-indoor-informations'] !== undefined) {
          editData['sl-indoor-informations'].push(tmp);
        }
      });
      groupInfo['slif-indoor-informations'].forEach(slifInfo => {
        const tmp = {
          'demand-level': slifInfo['demand-level'],
          'unit-id': slifInfo['unit-id'],
        };
        if (editData['slif-indoor-informations'] !== undefined) {
          editData['slif-indoor-informations'].push(tmp);
        }
      });
    });
    editData['indoor-unit'] = controlIndoorSwitching;
    editData['shift-temperature'] = shiftTemp;

    patchDemandSetting(props.allDemandInfo['site-id'], editData)
      .then(() => {
        getDemandSetting(props.allDemandInfo['site-id']).then(response => {
          props.setAllDemandInfo(response);
          props.setIsOpenIndoorDialog(false);
          setDemandGroupInfo(sortUnitList(response['group-informations']));
          setStateButtonAllDemandLevel(null);
          props.setUnitCount(prevCount => ({
            ...prevCount,
            iu: countIndoorUnit(response['group-informations']),
          }));
        });
      })
      .catch(() => {
        setIsOpenErrorDialog(true);
      })
      .finally(() => {
        setSaveBtnDisabled(false);
        setAPIWait(false);
      });
  };

  /**
   * 室内機のデマンドレベル別のユニット数を計算する関数
   * @param demandGroupInfo 室内機のデマンド情報
   * @returns 室内機のユニット数
   */
  const countIndoorUnit = (
    demandGroupInfo: apiIF.res.getDemandSetting['group-informations']
  ) => {
    let unitIndoorCount = {
      lv0: 0,
      lv1: 0,
      lv2: 0,
      lv3: 0,
      all: 0,
    };
    if (props.allDemandInfo !== null) {
      demandGroupInfo.forEach(groupInfo => {
        //SL
        groupInfo['sl-indoor-informations'].forEach(slInfo => {
          switch (slInfo['demand-level']) {
            //Level0
            case 'level0':
              unitIndoorCount.lv0++;
              break;
            //Level1
            case 'level1':
              unitIndoorCount.lv1++;
              break;
            //Level2
            case 'level2':
              unitIndoorCount.lv2++;
              break;
            //Level3
            case 'level3':
              unitIndoorCount.lv3++;
              break;
          }
        });
        //SLIF
        groupInfo['slif-indoor-informations'].forEach(slifInfo => {
          switch (slifInfo['demand-level']) {
            //Level0
            case 'level0':
              unitIndoorCount.lv0++;
              break;
            //Level1
            case 'level1':
              unitIndoorCount.lv1++;
              break;
            //Level2
            case 'level2':
              unitIndoorCount.lv2++;
              break;
            //Level3
            case 'level3':
              unitIndoorCount.lv3++;
              break;
          }
        });
      });
      unitIndoorCount.all =
        unitIndoorCount.lv0 +
        unitIndoorCount.lv1 +
        unitIndoorCount.lv2 +
        unitIndoorCount.lv3;
    }
    return unitIndoorCount;
  };

  return (
    <>
      <Dialog
        open={props.isOpenIndoorDialog}
        aria-labelledby="form-dialog-title"
        maxWidth="md">
        <DialogTitle id="form-dialog-title">
          {trans('IndoorUnitDemandControlDialog.title')}
        </DialogTitle>
        <DialogContent>
          <Stack spacing={2}>
            {/* 有効/無効設定 */}
            <Box>
              {trans('IndoorUnitDemandControlDialog.modeSetting.title')}
              <Grid container alignItems={'center'} columnSpacing={1} ml={1}>
                <Grid item xs={'auto'}>
                  {trans('IndoorUnitDemandControlDialog.modeSetting.item')}:
                </Grid>
                <Grid
                  item
                  xs={'auto'}
                  className="demandSetting__dialogContents__mode__value">
                  {activeDisp[controlIndoorSwitching]}
                </Grid>
                <Grid item xs={'auto'}>
                  <Switch
                    disabled={props.demandImplementation === true}
                    checked={controlIndoorSwitching === 'active'}
                    edge="end"
                    size="medium"
                    id="indoor"
                    onChange={handleChangeAutoSwitching}
                  />
                </Grid>
              </Grid>
            </Box>
            {/* レベルのパラメーター設定 */}
            <Box>
              {trans('IndoorUnitDemandControlDialog.parameterSetting.title')}
              <List className="demandSetting__dialogContents__parameterSetting">
                <ListItem>
                  <Grid container>
                    <Grid item xs="auto">
                      {trans(
                        'IndoorUnitDemandControlDialog.parameterSetting.level1.description'
                      )}
                      &nbsp;
                      {trans(
                        'IndoorUnitDemandControlDialog.parameterSetting.level1.plusMinus'
                      )}
                    </Grid>
                    <Grid
                      item
                      xs="auto"
                      className="demandSetting__dialogContents__parameterSetting__selectBox">
                      <TextFieldSelectBox
                        disabled={props.demandImplementation === true}
                        native={true}
                        choices={pullDownMenu.DEMAND_SHIFT_TEMP}
                        setValue={setShiftTemp}
                        default={shiftTemp}
                        id={`EditTempSelect`}
                        class="demandSetting__dialogContents__parameterSetting__selectBox--shift-temp"
                      />
                    </Grid>
                    <Grid item xs="auto">
                      {trans(
                        'IndoorUnitDemandControlDialog.parameterSetting.level1.tempUnit'
                      )}
                    </Grid>
                  </Grid>
                </ListItem>
                <ListItem>
                  {`${trans(
                    'IndoorUnitDemandControlDialog.parameterSetting.level2'
                  )}`}
                </ListItem>
                <ListItem>
                  {trans(
                    'IndoorUnitDemandControlDialog.parameterSetting.level3'
                  )}
                </ListItem>
              </List>
            </Box>
            {/** ユニット毎のレベル設定 */}
            <Box className="demandSetting__dialogContents__levelSetting">
              <Box>
                {trans('IndoorUnitDemandControlDialog.unitSetting.title')}
              </Box>
              <Box className="demandSetting__dialogContents__levelSetting__description">
                {trans('IndoorUnitDemandControlDialog.unitSetting.subTitle')}
              </Box>
              {/** テーブル */}
              <Box className="demandSetting__dialogContents__levelSetting__table">
                <Box className="demandSetting__dialogContents__levelSetting__table__frame">
                  {/** ヘッダ */}
                  <Grid
                    container
                    justifyContent="left"
                    className="demandSetting__dialogContents__levelSetting__table__frame__header">
                    <Grid
                      item
                      xs={gridXsUnitName}
                      className="demandSetting__dialogContents__levelSetting__table__frame__header__item">
                      {trans(
                        'IndoorUnitDemandControlDialog.unitSetting.table.header.name'
                      )}
                    </Grid>
                    <Grid
                      item
                      xs={gridXsDemandControlLevel}
                      className="demandSetting__dialogContents__levelSetting__table__frame__header__item">
                      {trans(
                        'IndoorUnitDemandControlDialog.unitSetting.table.header.level'
                      )}
                    </Grid>
                  </Grid>
                  {/** 内容 */}
                  <Box>
                    {/** 全ユニット */}
                    <Grid
                      container
                      alignItems="center"
                      className="demandSetting__dialogContents__levelSetting__table__frame__data__all">
                      <Grid
                        item
                        xs={gridXsUnitName}
                        className="demandSetting__dialogContents__levelSetting__table__frame__data__all__category">
                        {trans(
                          'IndoorUnitDemandControlDialog.unitSetting.table.data.all'
                        )}
                      </Grid>
                      <Grid
                        item
                        xs={gridXsDemandControlLevel}
                        className="demandSetting__dialogContents__levelSetting__table__frame__data__all__value">
                        <ToggleButtonGroup
                          value={stateButtonAllDemandLevel}
                          exclusive
                          onChange={onClickAllDemand}
                          fullWidth={true}
                          size={'small'}
                          aria-label="OutdoorAllDemandLevel"
                          className="demandSetting__dialogContents__levelSetting__table__frame__data__button">
                          {levelAll.map(level => (
                            <ToggleButton
                              disabled={props.demandImplementation === true}
                              key={`mv-btnOutdoorAll${level}`}
                              value={level}
                              className="demandSetting__dialogContents__levelSetting__table__frame__data__button__buttonName">
                              {levelDisp[level]}
                            </ToggleButton>
                          ))}
                        </ToggleButtonGroup>
                      </Grid>
                    </Grid>
                    {/** フロア、グループ毎 */}
                    {demandGroupInfo.map((groupInfo, groupIndex) => {
                      if (
                        groupInfo['sl-indoor-informations'].length === 0 &&
                        groupInfo['slif-indoor-informations'].length === 0
                      ) {
                        return <Box key={groupIndex}></Box>;
                      } else {
                        return (
                          <Box key={groupIndex}>
                            <Grid
                              container
                              alignItems="center"
                              className="demandSetting__dialogContents__levelSetting__table__frame__data__group">
                              <Grid
                                item
                                xs={12}
                                title={groupInfo['group-name']}
                                className="demandSetting__dialogContents__levelSetting__table__frame__data__category">
                                {buildingBasementName[groupInfo['floor']]} /
                                {groupInfo['group-name']}
                              </Grid>
                            </Grid>
                            {groupInfo['sl-indoor-informations'].map(
                              slIndoor => {
                                return (
                                  <UnitSet
                                    //キーをユニットIDで指定
                                    key={slIndoor['unit-id']}
                                    type={'sl-indoor'}
                                    unitName={slIndoor['indoor-unit-name']}
                                    demandGroupInfo={demandGroupInfo}
                                    setDemandGroupInfo={setDemandGroupInfo}
                                    targetUnitID={slIndoor['unit-id']}
                                    stateButtonDemandLevel={
                                      slIndoor['demand-level']
                                    }
                                    levelAll={levelAll}
                                    levelDisp={levelDisp}
                                    controlIndoorSwitching={
                                      controlIndoorSwitching
                                    }
                                    demandImplementation={
                                      props.demandImplementation
                                    }
                                  />
                                );
                              }
                            )}
                            {groupInfo['slif-indoor-informations'].map(
                              slIndoor => {
                                return (
                                  <UnitSet
                                    //キーをユニットIDで指定
                                    key={slIndoor['unit-id']}
                                    type={'slif-indoor'}
                                    unitName={slIndoor['unit-name']}
                                    interfaceType={slIndoor['interface-type']}
                                    demandGroupInfo={demandGroupInfo}
                                    setDemandGroupInfo={setDemandGroupInfo}
                                    targetUnitID={slIndoor['unit-id']}
                                    stateButtonDemandLevel={
                                      slIndoor['demand-level']
                                    }
                                    levelAll={levelAll}
                                    levelDisp={levelDisp}
                                    controlIndoorSwitching={
                                      controlIndoorSwitching
                                    }
                                    demandImplementation={
                                      props.demandImplementation
                                    }
                                  />
                                );
                              }
                            )}
                          </Box>
                        );
                      }
                    })}
                  </Box>
                </Box>
              </Box>
            </Box>
          </Stack>
        </DialogContent>
        <DialogActions>
          {/* CANCEL/SAVEボタンの設定 */}
          <SecondaryButton
            disabled={apiWait}
            onClick={handleCancel}
            buttonText={trans('IndoorUnitDemandControlDialog.button.cancel')}
          />
          <PrimaryButton
            disabled={saveBtnDisabled || props.demandImplementation === true}
            onClick={onClickSave}
            buttonText={trans('IndoorUnitDemandControlDialog.button.save')}
            apiWait={apiWait}
          />
        </DialogActions>
      </Dialog>
      <APIErrorMessage
        open={isOpenErrorDialog}
        setOpen={setIsOpenErrorDialog}
        id={'mv-IndoorDemandSettingErrDlgBtn'}
      />
    </>
  );
};

/**
 * 各ユニット毎のデマンドレベル設定用のボタンを表示する関数
 * @param props
 * @returns 表示画面
 */
const UnitSet = (props: {
  key: string; //キー
  type: 'sl-indoor' | 'slif-indoor'; //室内機ユニットのタイプ
  unitName: string; //ユニット名
  interfaceType?: string; //インターフェースのタイプ(SLIFのみ)
  demandGroupInfo: apiIF.res.getDemandSetting['group-informations']; //デマンド情報
  setDemandGroupInfo: React.Dispatch<
    React.SetStateAction<apiIF.res.getDemandSetting['group-informations']> //デマンド情報の設定関数
  >;
  targetUnitID: string; //対象のユニットのID
  stateButtonDemandLevel: 'level0' | 'level1' | 'level2' | 'level3'; //デマンドレベルの情報
  levelAll: ('level1' | 'level2' | 'level3' | 'level0')[]; // Level0, level1, level2, level3の配列
  levelDisp: {level1: string; level2: string; level3: string; level0: string}; // ボタンに表示するレベルの文字列
  controlIndoorSwitching: 'active' | 'inactive';
  demandImplementation: boolean; //デマンド実施状況
}) => {
  /**
   * Levelボタンを押した時の処理
   * @param event ボタン押下時のイベント
   * @param selectedDemandLevel 選択したデマンドレベル
   */
  const onClickButton = (
    event: React.MouseEvent<HTMLElement>,
    selectedDemandLevel: 'level0' | 'level1' | 'level2' | 'level3' | null
  ) => {
    if (selectedDemandLevel !== null && props.demandGroupInfo !== null) {
      props.setDemandGroupInfo(
        props.demandGroupInfo.map(groupInfo => {
          if (props.type === 'sl-indoor') {
            return {
              ...groupInfo,
              'sl-indoor-informations': groupInfo['sl-indoor-informations'].map(
                slInfo => {
                  if (props.targetUnitID === slInfo['unit-id']) {
                    return {...slInfo, 'demand-level': selectedDemandLevel};
                  } else {
                    return slInfo;
                  }
                }
              ),
            };
          } else if (props.type === 'slif-indoor') {
            return {
              ...groupInfo,
              'slif-indoor-informations': groupInfo[
                'slif-indoor-informations'
              ].map(slifInfo => {
                if (props.targetUnitID === slifInfo['unit-id']) {
                  return {...slifInfo, 'demand-level': selectedDemandLevel};
                } else {
                  return slifInfo;
                }
              }),
            };
          } else {
            return groupInfo;
          }
        })
      );
    }
  };

  return (
    <Grid
      container
      alignItems="center"
      className="demandSetting__dialogContents__levelSetting__table__frame__data__unit">
      <Grid
        item
        xs={gridXsUnitName}
        title={props.unitName}
        className="demandSetting__dialogContents__levelSetting__table__frame__data__category">
        {props.unitName}
      </Grid>
      <Grid item xs={gridXsDemandControlLevel}>
        {/**各デマンドレベルのトグルボタン */}
        <ToggleButtonGroup
          value={props.stateButtonDemandLevel}
          exclusive
          onChange={onClickButton}
          fullWidth={true}
          size={'small'}
          aria-label="IndoorDemandLevel"
          className="demandSetting__dialogContents__levelSetting__table__frame__data__button">
          {props.levelAll.map(level => (
            <ToggleButton
              key={`mv-btnIndoor${level}`}
              disabled={
                (props.interfaceType === 'other' &&
                  (level === 'level1' || level === 'level2')) ||
                props.demandImplementation === true
              }
              value={level}
              className="demandSetting__dialogContents__levelSetting__table__frame__data__button__buttonName">
              {props.levelDisp[level]}
            </ToggleButton>
          ))}
        </ToggleButtonGroup>
      </Grid>
    </Grid>
  );
};

export default IndoorUnitDemandControlDialog;
